import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Download, ChevronUp, ChevronDown } from 'lucide-react';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from '../../components/ui/table'; // Adjusted path
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'; // Adjusted path
import { Button } from '../../components/ui/button'; // Adjusted path
import { formatDateString } from '../../lib/utils'; // Adjusted path

interface WaiverReport {
  id: string;
  player_id: string;
  parent_id: string;
  waiver_name: string;
  result_code: string;
  created_at: string;
  player_first_name: string;
  player_last_name: string;
  parent_first_name: string;
  parent_last_name: string;
  parent_email: string;
  age_group_id?: string;
  age_group?: {
    id: string;
    group_name: string;
    sex: string;
  };
}

type SortField = 'playerName' | 'parentName' | 'date';
type SortDirection = 'asc' | 'desc';

export default function DeclinedPhotoWaiversReport() {
  const [declinedWaivers, setDeclinedWaivers] = useState<WaiverReport[]>([]);
  const [loadingWaivers, setLoadingWaivers] = useState(false);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('playerName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  async function fetchDeclinedPhotoWaivers() {
    setLoadingWaivers(true);
    setError('');
    setReportGenerated(true);
    setDeclinedWaivers([]); // Reset waivers before fetching

    try {
      const { data, error: fetchError } = await supabase
        .from('waiver_log')
        .select(`
          id,
          player_id,
          parent_id,
          waiver_name,
          result_code,
          created_at,
          players!inner(first_name, last_name, age_group_id, age_group:age_groups(*))
        `)
        .eq('waiver_name', 'PHOTO_RELEASE_WAIVER')
        .eq('result_code', 'disagreed');

      if (fetchError) {
        throw fetchError;
      }
      
      if (data && data.length > 0) {
        const parentIds = [...new Set(data.map((item: any) => item.parent_id))];
        
        const { data: parentData, error: parentError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email')
          .in('id', parentIds);
        
        if (parentError) {
          throw parentError;
        }
        
        const parentMap = (parentData || []).reduce((acc: any, parent: any) => {
          acc[parent.id] = parent;
          return acc;
        }, {});
        
        const formattedData = (data || []).map((item: any) => {
          const parent = parentMap[item.parent_id] || {};
          return {
            id: item.id,
            player_id: item.player_id,
            parent_id: item.parent_id,
            waiver_name: item.waiver_name,
            result_code: item.result_code,
            created_at: item.created_at,
            player_first_name: item.players?.first_name || '',
            player_last_name: item.players?.last_name || '',
            parent_first_name: parent.first_name || '',
            parent_last_name: parent.last_name || '',
            parent_email: parent.email || '',
            age_group_id: item.players?.age_group_id || null,
            age_group: item.players?.age_group || null
          };
        });

        setDeclinedWaivers(formattedData);
      } else {
         setDeclinedWaivers([]); // Ensure empty if no data
      }
    } catch (err: any) {
      console.error('Error fetching declined photo waivers:', err);
      setError(err.message || 'Failed to fetch report');
    } finally {
      setLoadingWaivers(false);
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedWaivers = () => {
    return [...declinedWaivers].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'playerName':
          const aPlayerName = `${a.player_last_name}, ${a.player_first_name}`;
          const bPlayerName = `${b.player_last_name}, ${b.player_first_name}`;
          comparison = aPlayerName.localeCompare(bPlayerName);
          break;
        case 'parentName':
          const aParentName = `${a.parent_last_name}, ${a.parent_first_name}`;
          const bParentName = `${b.parent_last_name}, ${b.parent_first_name}`;
          comparison = aParentName.localeCompare(bParentName);
          break;
        case 'date':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const exportCSV = () => {
    if (declinedWaivers.length === 0) return;
    
    const headers = ['Player Name', 'Parent Name', 'Parent Email', 'Age Group', 'Date Declined'];
    const sortedData = getSortedWaivers(); // Use sorted data for export

    const csvContent = [
      headers.join(','),
      ...sortedData.map(waiver => [
        `"${waiver.player_first_name} ${waiver.player_last_name}"`,
        `"${waiver.parent_first_name} ${waiver.parent_last_name}"`,
        `"${waiver.parent_email}"`,
        `"${waiver.age_group ? `${waiver.age_group.sex === 'male' ? 'Boys' : 'Girls'} ${waiver.age_group.group_name}` : 'Not Assigned'}"`,
        `"${formatDateString(waiver.created_at, 'en-US', 'short')}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `declined_photo_waivers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="ml-1 h-4 w-4 text-gray-400" />;
    return sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Declined Photo Waivers</CardTitle>
        <CardDescription>Players whose parents declined the photo release waiver.</CardDescription>
        <div className="flex justify-end space-x-2">
           <Button onClick={fetchDeclinedPhotoWaivers} disabled={loadingWaivers}>
            {loadingWaivers ? 'Generating...' : 'Generate Report'}
          </Button>
          {reportGenerated && declinedWaivers.length > 0 && (
            <Button variant="outline" onClick={exportCSV} disabled={declinedWaivers.length === 0}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loadingWaivers && <p>Loading...</p>}
        {error && <p className="text-red-500">Error: {error}</p>}
        {reportGenerated && !loadingWaivers && declinedWaivers.length === 0 && (
          <p>No declined photo waivers found.</p>
        )}
        {reportGenerated && !loadingWaivers && declinedWaivers.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer" onClick={() => handleSort('playerName')}>
                  Player Name <SortIcon field="playerName" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('parentName')}>
                  Parent Name <SortIcon field="parentName" />
                </TableHead>
                <TableHead>Parent Email</TableHead>
                <TableHead>Age Group</TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('date')}>
                  Date Declined <SortIcon field="date" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getSortedWaivers().map((waiver) => (
                <TableRow key={waiver.id}>
                  <TableCell>{`${waiver.player_first_name} ${waiver.player_last_name}`}</TableCell>
                  <TableCell>{`${waiver.parent_first_name} ${waiver.parent_last_name}`}</TableCell>
                  <TableCell>{waiver.parent_email}</TableCell>
                   <TableCell>{waiver.age_group ? `${waiver.age_group.sex === 'male' ? 'Boys' : 'Girls'} ${waiver.age_group.group_name}` : 'Not Assigned'}</TableCell>
                  <TableCell>{formatDateString(waiver.created_at, 'en-US', 'short')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
